import { useState, useEffect } from 'react';
import RefineBtn from './RefineBtn';
import { refineText } from '../lib/api';
import type { RefineStyle } from '../lib/api';

export interface ResponseItem {
  type: string;
  text: string;
}

export interface RefineOption {
  key: RefineStyle;
  label: string;
}

interface Props {
  item: ResponseItem;
  refineOptions: RefineOption[];
  caveat?: string;
  canSubmit?: boolean;
  onSubmit?: (text: string, type: string) => void;
}

export default function ResponseCard({
  item,
  refineOptions,
  caveat,
  canSubmit,
  onSubmit,
}: Props) {
  const [text, setText] = useState(item.text);
  const [loading, setLoading] = useState<RefineStyle | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setText(item.text);
    setError(null);
  }, [item.text]);

  const refine = async (style: RefineStyle) => {
    setLoading(style);
    setError(null);
    try {
      const result = await refineText(text, style);
      setText(result);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'Could not refine. Try again?');
    } finally {
      setLoading(null);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError('Clipboard blocked by the browser.');
    }
  };

  return (
    <div
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: '22px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.12em',
          color: 'var(--tag)',
          textTransform: 'uppercase',
        }}
      >
        {item.type}
      </div>
      <p
        style={{
          fontSize: 15,
          lineHeight: 1.6,
          color: 'var(--text-body)',
          textWrap: 'pretty',
          minHeight: 60,
        }}
      >
        {loading ? (
          <span style={{ color: '#666', fontStyle: 'italic' }}>Rewriting…</span>
        ) : (
          text
        )}
      </p>
      {caveat && !loading && (
        <div
          style={{
            fontSize: 11,
            color: '#8a8580',
            background: '#1f1d1a',
            border: '1px solid #2e2a26',
            borderRadius: 4,
            padding: '6px 10px',
            lineHeight: 1.45,
          }}
        >
          {caveat}
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: '#e29090' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <RefineBtn label="Copy" onClick={copy} justCopied={copied} />
        {refineOptions.map((opt) => (
          <RefineBtn
            key={opt.key}
            label={opt.label}
            onClick={() => refine(opt.key)}
            loading={loading === opt.key}
          />
        ))}
        {canSubmit && onSubmit && (
          <RefineBtn label="Submit to HoF" onClick={() => onSubmit(text, item.type)} />
        )}
      </div>
    </div>
  );
}
